import React, { useEffect, useState } from 'react';

import { MoreVert, AttachMoney } from '@mui/icons-material';
import { List, ListItemSecondaryAction, IconButton, Typography } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { commonStyle } from '../components/commonStyle';
import ErrorSnack from '../components/ErrorSnack';
import DropDownMenu, { CurrentDialog } from '../components/Income/DropDownMenu';
import IncomeEstimationTable from '../components/Income/IncomeEstimationTable';
import JSONViewerDialogCommon from '../components/JSONViewerDialog';
import LinkLogo from '../components/LinkLogo';
import StatusIcon from '../components/StatusIcon';
import {
  StyledAvatar,
  StyledListItem,
  StyledListItemAvatar,
  StyledListItemText,
  StyledRootContainer,
} from '../components/styledComponents';
import TopBar from '../components/TopBar';
import useLoginIdentityId from '../hooks/data/useLoginIdentityId';
import useValidToken from '../hooks/useValidToken';
import { Account, AccountsResponse, IncomeStream } from '../models';
import { getApiClient, getValue, isAuthorizationError } from '../services';
import { isProductEndStatus } from './Utilities';

type ParamTypes = {
  accountId: string;
};

interface Props {
  topLevelAccounts?: AccountsResponse;
}

export default function IncomeEstimationByAccount({ topLevelAccounts }: Props): JSX.Element {
  const classes = commonStyle();
  const history = useHistory();
  const client = getApiClient();
  const { accountId } = useParams<ParamTypes>();

  const [dialog, setDialog] = useState<CurrentDialog>(CurrentDialog.NONE);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackOpen, setSnackOpen] = useState(false);

  const [currAccount] = useState<Account | undefined>(
    topLevelAccounts?.accounts?.find((acc) => acc.account_id === accountId),
  );

  const handleClose = () => {
    handleMenuClose();
    setDialog(CurrentDialog.NONE);
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const showErrorMessage = () => {
    setSnackOpen(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewClick = (view: CurrentDialog) => {
    setDialog(view);
  };

  const userToken = String(getValue('userToken'));
  const { validToken } = useValidToken(userToken);
  const { data: loginIdentity, error: loginIdentityError, mutate: mutateLoginIdentity } = useLoginIdentityId(userToken);
  const loginIdentityStatus = loginIdentity?.login_identity.status ?? '';
  const incomeStatus = loginIdentity?.login_identity.product_status.income_estimation?.status ?? '';
  const incomeStatusDetail = loginIdentity?.login_identity.product_status.income_estimation?.status_details ?? '';

  const {
    data: incomeResponse,
    error: incomeResponseError,
    mutate: mutateIncomeResponse,
  } = useSWR(
    isProductEndStatus(loginIdentity?.login_identity.product_status.income_estimation?.status ?? '')
      ? [`/income`, userToken]
      : null,
    (_: string, token: string) => client.getIncomeEstimation(token),
  );

  const incomeLoading = !incomeResponse && !incomeResponseError;
  const loading = incomeLoading;

  const mutate = () => {
    mutateLoginIdentity();
    mutateIncomeResponse();
  };

  useEffect(() => {
    if (validToken === undefined) {
      return;
    }
    if (!validToken) {
      // Token invlaid should route to login page
      history.push('/login');
    }
  }, [validToken, history]);

  useEffect(() => {
    const errors = [incomeResponseError, loginIdentityError];

    if (errors.every((error) => error === undefined)) {
      return;
    }

    if (errors.some((error) => isAuthorizationError(error))) {
      history.push('/login');
      return;
    }

    showErrorMessage();
  }, [incomeResponseError, loginIdentityError, history]);

  // To clean up some conditional rendering

  let targetIncome: IncomeStream | undefined;
  let totalAvgIncome = '';
  if (incomeResponse && incomeResponse.income && incomeResponse.income.length > 0) {
    targetIncome = incomeResponse.income[0].income_streams?.find((item: IncomeStream) => {
      return item.account_id === accountId;
    });

    totalAvgIncome = `Estimated Monthly Income:${targetIncome?.estimated_monthly_income?.amount.toLocaleString(
      undefined,
      { maximumFractionDigits: 2, minimumFractionDigits: 2 },
    )}(${targetIncome?.estimated_monthly_income?.currency})`;
  }

  return (
    <>
      <div className={classes.main}>
        <StyledRootContainer>
          <TopBar title="IncomeEstimation" showBackButton={true} />
          <LinkLogo title={`Income Estimation for ${currAccount?.account_name}`} component={AttachMoney} />
          <List>
            <StyledListItem sx={{ height: '80px' }} key={0} button component="a">
              <StyledListItemAvatar>
                <StyledAvatar
                  alt={loginIdentity?.institution?.institution_id}
                  src={`/img/square/${loginIdentity?.institution?.institution_id}.svg`}
                />
              </StyledListItemAvatar>
              <StyledListItemText
                sx={{ wordWrap: 'break-word' }}
                primary={
                  incomeResponse
                    ? incomeResponse?.institution?.institution_name
                    : topLevelAccounts?.institution.institution_name
                }
                secondary={<Typography>{totalAvgIncome}</Typography>}
              />
              <ListItemSecondaryAction>
                <StatusIcon status={loginIdentityStatus} />
                <IconButton
                  disabled={
                    !isProductEndStatus(loginIdentity?.login_identity.product_status.income_estimation?.status ?? '') ||
                    loading
                  }
                  edge="end"
                  aria-label="action"
                  onClick={handleMenuClick}
                  size="large"
                >
                  <MoreVert />
                </IconButton>
              </ListItemSecondaryAction>
            </StyledListItem>
          </List>

          <IncomeEstimationTable
            incomeStatus={incomeStatus}
            incomeStatusDetails={incomeStatusDetail}
            isLoading={incomeLoading}
            incomeEstimate={targetIncome?.estimated_monthly_income}
            monthlyIncome={targetIncome?.monthly_history}
          />

          <JSONViewerDialogCommon
            open={dialog === CurrentDialog.INCOME_JSON}
            json={incomeResponse}
            onClose={handleClose}
          />
          <DropDownMenu anchorElement={anchorEl} onViewClick={handleViewClick} onClose={handleMenuClose} />
        </StyledRootContainer>
      </div>
      <ErrorSnack open={snackOpen} handleClose={handleSnackClose} retry={mutate} />
    </>
  );
}
